<script setup lang="ts">
import { useGameStore } from '@/stores/gameStore'
import { computed, ref } from 'vue'
import { RouterLink } from 'vue-router'
import { useShipStore } from '@/stores/shipStore.ts'
import { useTableStore } from '@/stores/tableStore.ts'
import { usePlayerCreditStore } from '@/stores/playerCreditStore.ts'
import { usePlayerFlagsStore } from '@/stores/playerFlagsStore.ts'
import { usePlanetStore } from '@/stores/planetStore.ts'
import GenericIconWithColoredNumber from '@/components/Elements/Images/Icons/GenericIconWithColoredNumber.vue'
import GenericIconWithNumber from '@/components/Elements/Images/Icons/GenericIconWithNumber.vue'
import CountdownTimer from '@/components/MainInterface/Footer/CountdownTimer.vue'
import HoursOnline from '@/components/MainInterface/Footer/HoursOnline.vue'
import FooterResearch from '@/components/MainInterface/Footer/FooterResearch.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const gameStore = useGameStore()
const planetStore = usePlanetStore()
const shipStore = useShipStore()
const tableStore = useTableStore()
const playerCreditStore = usePlayerCreditStore()
const playerFlagsStore = usePlayerFlagsStore()

const showVipToolTip = ref(false)

const creditsOutput = computed(() => {
  if (gameStore.websocketInitialSyncCompleted) {
    return playerCreditStore.calculateCreditsPerTurn()
  }
  return 0
})

const isTutorialMinimized = computed(() => {
  return playerFlagsStore.getFlag('tutorial_hidden') === 'true' &&
         playerFlagsStore.getFlag('tutorial_disable_tutorial') !== 'true'
})

const restoreTutorial = async () => {
  await playerFlagsStore.setFlag('tutorial_hidden', 'false')
  // Emit an event or trigger modal restoration
  window.dispatchEvent(new CustomEvent('restore-tutorial'))
}
</script>

<template>
  <!-- Tutorial Icon (fixed position, outside footer flow) -->
  <div 
    v-if="isTutorialMinimized"
    @click="restoreTutorial"
    class="fixed bottom-0.5 right-1 z-50 p-2 cursor-pointer hover:scale-110"
  >
    <svg
      class="w-5 h-5 text-blue-400 hover:text-blue-300"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  </div>

  <div class="footer bg-slate-700 flex items-center justify-between text-slate-300 text-sm w-full">
    <span class="pl-2 pr-2 hidden sm:block">
      <span class="flex items-center justify-between">
        <img
          :src="'https://wsrv.nl/?url=' + gameStore.player?.profileImage + '&w=36&h=36'"
          class="w-8 h-8 rounded"
          alt="Profile image"
        />
        <span class="ps-2 hidden md:inline">{{ gameStore.player?.nickName }}</span>
      </span>
    </span>
    <div v-if="!gameStore.gameFinished" class="flex flex-grow items-center justify-center">
      <HoursOnline/>
      <CountdownTimer />
      <FooterResearch />
      <RouterLink :to="'/government/finances/'">
        <div class="bg-slate-700 p-0.5 ps-2 pe-2 rounded-md ms-1 flex pr-2">
          <GenericIconWithColoredNumber
            :amount="creditsOutput"
            type="credit"
            textColor="text-slate-300"
          />
        </div>
      </RouterLink>
      <RouterLink :to="'/planets/' + tableStore.planets.singleSelectedId">
        <div class="bg-slate-700 p-0.5 ps-2 pe-2 rounded-md ms-1 flex">
          <GenericIconWithNumber
            :amount="planetStore.planets.size"
            type="planet"
            textColor="text-slate-300"
          />
        </div>
      </RouterLink>
      <RouterLink :to="'/command/ships/' + tableStore.ships.singleSelectedId">
        <div class="bg-slate-700 p-0.5 ps-2 pe-2 rounded-md ms-1 flex">
          <GenericIconWithNumber
            :amount="shipStore.ships.size"
            type="ship"
            textColor="text-slate-300"
          />
        </div>
      </RouterLink>
      <RouterLink to="/government/cabinet" v-if="Number(gameStore.player?.vipTokens) > 0">
        <div v-if="showVipToolTip" class="current-tech bg-slate-800 border-1 border-slate-700 p-2">
          {{ t('government.cabinet.vipTokensFooterTooltip') }}
        </div>
        <div
          class="bg-slate-700 p-0.5 ps-2 pe-2 rounded-md ms-1 hidden sm:flex"
          @mouseenter="showVipToolTip = true"
          @mouseleave="showVipToolTip = false"
        >
          <GenericIconWithNumber
            :amount="Number(gameStore.player?.vipTokens)"
            type="vip"
            textColor="text-slate-300"
          />
        </div>
      </RouterLink>
    </div>
    
  </div>
</template>

<style scoped>
.footer {
  height: 2.5rem;
}
.current-tech {
  position: absolute;
  bottom: 2.5rem;
}
</style>
